import { Button as MuiButton, Typography } from '@mui/material'
import { styled } from '@mui/system'
import { colorPalette } from '../../../../../style/colorPalette'
import { useCallback, useRef, useState } from 'react'
import { SelectOption } from '../../../../../model/selectOption'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import { Button } from '../../../../../components/buttons'
import { intl } from '../../../../../../i18n'
import ProcessScopeSearchSelectPopper from './ProcessScopeSearchSelectPopper'
import { useProcessScopeSearchSelect } from './useProcessScopeSearchSelect'

type Props = {
  title: string
  value: string[] | undefined
  options: SelectOption[]
  onChange: (newValue: string[] | string | undefined) => void
}

const ProcessScopeSearchSelect = ({
  title,
  value,
  options,
  onChange,
}: Props) => {
  const [openPopper, setOpenPopper] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)

  const {
    searchText,
    setSearchText,
    filteredOptions,
    temporarySelection,
    toggleItem,
    applySelection,
    resetToInitial,
  } = useProcessScopeSearchSelect(options)

  const handleOpen = useCallback(() => {
    resetToInitial(value || [])
    setOpenPopper(true)
  }, [value, resetToInitial])

  const handleClose = useCallback(() => {
    setOpenPopper(false)
  }, [])

  const handleApply = useCallback(() => {
    const selected = applySelection()
    onChange(selected.length > 0 ? selected : undefined)
    setOpenPopper(false)
  }, [applySelection, onChange])

  const handleRemoveItem = useCallback(
    (itemValue: string) => {
      const newValue = (value || []).filter(v => v !== itemValue)
      onChange(newValue.length > 0 ? newValue : undefined)
    },
    [value, onChange]
  )

  const handleClearAll = useCallback(() => {
    onChange('')
  }, [onChange])

  return (
    <SelectContainer ref={anchorRef}>
      <Typography
        sx={{
          color: colorPalette.monotone[4],
          minWidth: '80px',
          marginRight: '10px',
        }}
      >
        {title}
      </Typography>
      <SelectedItemsContainer>
        {value && value.length >= 1 ? (
          <>
            {options
              .filter(option => (value || []).includes(option.value))
              .map(option => (
                <SelectedChip key={option.value}>
                  {option.iconUrl && <img src={option.iconUrl} />}
                  <Typography
                    sx={{
                      margin: 'auto 5px',
                      color: colorPalette.monotone[5],
                    }}
                  >
                    {option.name}
                  </Typography>
                  <Button
                    colorPattern="monotone"
                    sx={{
                      border: 'none',
                      background: colorPalette.monotone[0],
                      width: '10px',
                      height: '24px',
                      borderRadius: '15px',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleRemoveItem(option.value)}
                  >
                    <ClearRoundedIcon
                      sx={{
                        color: colorPalette.monotone[4],
                        height: '13px',
                        width: '13px',
                      }}
                    />
                  </Button>
                </SelectedChip>
              ))}
            <MuiButton
              sx={{ minWidth: '20px', margin: '3px auto 3px 0' }}
              onClick={handleClearAll}
            >
              <ClearRoundedIcon sx={{ color: colorPalette.monotone[4] }} />
            </MuiButton>
          </>
        ) : (
          <Button
            colorPattern="monotone"
            size="s"
            sx={{
              borderRadius: '15px',
              border: `1px dashed ${colorPalette.monotone[2]}`,
              background: colorPalette.monotone[0],
              color: colorPalette.monotone[3],
              margin: '4px auto 4px 15px',
              cursor: 'pointer',
            }}
            onClick={handleOpen}
          >
            <Typography>
              {intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.click.select',
              })}
            </Typography>
            <AddRoundedIcon sx={{ height: '12px', width: '12px' }} />
          </Button>
        )}
      </SelectedItemsContainer>
      <MuiButton
        sx={{ minWidth: '20px', margin: '0 0 0 auto' }}
        onClick={() => {
          if (openPopper) {
            handleClose()
          } else {
            handleOpen()
          }
        }}
      >
        {openPopper ? (
          <ExpandLess sx={{ color: colorPalette.monotone[4] }} />
        ) : (
          <ExpandMore sx={{ color: colorPalette.monotone[4] }} />
        )}
      </MuiButton>
      {openPopper && (
        <ProcessScopeSearchSelectPopper
          open={openPopper}
          anchorEl={anchorRef.current || undefined}
          options={filteredOptions}
          temporarySelection={temporarySelection}
          searchText={searchText}
          onSearchTextChange={setSearchText}
          onToggleItem={toggleItem}
          onApply={handleApply}
          onClose={handleClose}
        />
      )}
    </SelectContainer>
  )
}

const SelectContainer = styled('div')({
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: '5px 10px',
  height: '100%',
})

const SelectedItemsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  maxWidth: '416px',
  flexWrap: 'wrap',
  flex: 1,
})

const SelectedChip = styled('div')({
  display: 'flex',
  padding: '3px 5px',
  borderRadius: '15px',
  margin: '3px 5px',
  background: colorPalette.monotone[1],
  alignItems: 'center',
})

export default ProcessScopeSearchSelect
